import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { useProcessorById, useUpdateProcessor, useTagadapayAccounts } from '@/lib/api/processors'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/components/ui/use-toast'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { TagadaPayRouterEditSkeleton } from '../components/tagadapay-router-edit-skeleton'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import {
    ProcessorThreeDSModeField,
    THREE_DS_MODE_DEFAULT,
    type ThreeDSMode,
} from '../../components/processor-threeds-mode-field'
import {
    ProcessorApmSettings,
    initApmSettingsFromOptions,
    getMorPublishableKey,
    ApmSettings,
} from '../../components/processor-apm-settings'
import { TagadaPayAccountField } from '../components/tagadapay-account-field'

const TagadaPayRouterEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const processorExit = useProcessorEditExit()
    const { t } = useTranslation('processors')
    const { toast } = useToast()

    const { data: processor, isLoading } = useProcessorById(id!)
    const { data: tagadapayAccounts } = useTagadapayAccounts()
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        tagadapayAccountId: '',
        descriptor: '',
        testMode: false,
        dryMode: false,
        threeDSMode: THREE_DS_MODE_DEFAULT as ThreeDSMode,
    })
    const [apmSettings, setApmSettings] = useState<ApmSettings>(() =>
        initApmSettingsFromOptions('tagadapay-router', undefined)
    )
    const [expressPublishableKey, setExpressPublishableKey] = useState<string>('')

    // The router delegates to the acquirer of its linked TagadaPay account. When
    // that acquirer is Stripe, the router behaves like the old `tagadapay` MoR:
    // Stripe Express Checkout (Apple/Google Pay) using the shared MoR platform key.
    const payfacProvider =
        tagadapayAccounts?.find((a) => a.id === formData.tagadapayAccountId)?.payfacProvider ?? undefined
    const isStripeRouter = payfacProvider === 'stripe'

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                tagadapayAccountId: options?.tagadapayAccountId || '',
                descriptor: options?.statementDescriptor || '',
                testMode: options?.testMode === true,
                dryMode: options?.dryMode === true,
                threeDSMode: (options?.threeDSMode as ThreeDSMode) || THREE_DS_MODE_DEFAULT,
            })
        }
    }, [processor])

    // Re-seed APM + express settings once the linked account's acquirer is known
    // (payfacProvider resolves after the account id lands in formData). For a
    // Stripe-backed router the express key defaults to the shared TagadaPay MoR
    // platform key — never entered manually, matching the old `tagadapay` MoR.
    useEffect(() => {
        if (!processor) return
        const options = processor.options as any
        setApmSettings(
            initApmSettingsFromOptions(
                'tagadapay-router',
                options?.apmSettings as Record<string, Record<string, unknown>> | undefined,
                payfacProvider,
            )
        )
        setExpressPublishableKey(
            options?.expressPublishableKey ||
                (payfacProvider === 'stripe' ? getMorPublishableKey('tagadapay') : ''),
        )
    }, [processor, payfacProvider])

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setValidationError(t('nameRequired'))
            return
        }
        if (!formData.tagadapayAccountId.trim()) {
            setValidationError(t('tagadapayRouter.accountIdRequired'))
            return
        }

        setValidationError(null)

        try {
            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    type: processor!.type,
                    name: formData.name,
                    enabled: formData.enabled,
                    options: {
                        tagadapayAccountId: formData.tagadapayAccountId,
                        statementDescriptor: formData.descriptor || undefined,
                        testMode: formData.testMode,
                        dryMode: formData.dryMode,
                        threeDSMode: formData.threeDSMode,
                        apmSettings,
                        expressPublishableKey:
                            isStripeRouter && expressPublishableKey ? expressPublishableKey : undefined,
                        // Ops-set, no UI: preserve across saves (options is replaced wholesale).
                        suppressWalletReceiptEmail:
                            (processor!.options as any)?.suppressWalletReceiptEmail || undefined,
                    },
                } as any,
            })

            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch (error) {
            console.error('Error saving processor:', error)
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    if (isLoading) {
        return (
            <div className="container mx-auto py-8">
                <TagadaPayRouterEditSkeleton />
            </div>
        )
    }

    if (!processor || processor.type !== 'tagadapay-router') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('tagadapayRouter.editTitle')}
                onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <Tabs defaultValue="configuration">
                <TabsList className="mb-6">
                    <TabsTrigger value="configuration">{t('configuration')}</TabsTrigger>
                    <TabsTrigger value="payment-methods">{t('paymentMethods')}</TabsTrigger>
                </TabsList>

                <TabsContent value="configuration">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('generalSettings')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="name">{t('processorName')}</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="enabled"
                                        checked={formData.enabled}
                                        onCheckedChange={(checked) =>
                                            setFormData({ ...formData, enabled: checked === true })
                                        }
                                    />
                                    <Label htmlFor="enabled">{t('enabled')}</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="testMode"
                                        checked={formData.testMode}
                                        onCheckedChange={(checked) =>
                                            setFormData({ ...formData, testMode: checked === true })
                                        }
                                    />
                                    <Label htmlFor="testMode">{t('tagadapayRouter.sandboxTestMode')}</Label>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>{t('tagadapayRouter.configurationTitle')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <TagadaPayAccountField
                                    value={formData.tagadapayAccountId}
                                    onChange={(accountId) =>
                                        setFormData({ ...formData, tagadapayAccountId: accountId })
                                    }
                                    disabled={updateProcessorMutation.isPending}
                                />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>{t('statementDescriptor')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="descriptor">{t('descriptor')}</Label>
                                    {/* Read-only: the descriptor is set by Tagada, not by the
                                        merchant. The server keeps the stored value whatever this
                                        form sends (useCases/processors/preserveStatementDescriptor),
                                        so an editable input would only promise a change that never
                                        happens. */}
                                    <Input
                                        id="descriptor"
                                        value={formData.descriptor}
                                        readOnly
                                        disabled
                                        placeholder={t('tagadapayRouter.descriptorPlaceholder')}
                                    />
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {t('tagadapayRouter.descriptorManagedHint')}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <ProcessorThreeDSModeField
                            value={formData.threeDSMode}
                            onChange={(mode) => setFormData({ ...formData, threeDSMode: mode })}
                            disabled={updateProcessorMutation.isPending}
                        />

                        <ProcessorDryModeField
                            checked={formData.dryMode}
                            onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                            disabled={updateProcessorMutation.isPending}
                        />
                    </div>
                </TabsContent>

                <TabsContent value="payment-methods">
                    <ProcessorApmSettings
                        processorType="tagadapay-router"
                        payfacProvider={payfacProvider}
                        value={apmSettings}
                        onChange={setApmSettings}
                        expressPublishableKey={expressPublishableKey}
                        onExpressPublishableKeyChange={setExpressPublishableKey}
                        disabled={updateProcessorMutation.isPending}
                    />
                </TabsContent>
            </Tabs>
        </div>
    )
}

export const TagadaPayRouterEditPage = withRequiredPermission(TagadaPayRouterEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)
